// TablaPlanificacionesPorOrganizacion.tsx
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import PictureAsPdfSharpIcon from "@mui/icons-material/PictureAsPdfSharp";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Grid, IconButton, InputAdornment, Menu, MenuItem, Paper, Rating, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useProjectsContext } from "../../context/Projects/ProjectContext";
import { getProyectosByOrganizacionId } from "../../controllers/operaciones";
import { useAuthContext } from "../../hooks/Auth";
import { iDataProyectoPorUsuario } from "../../interfaces/iDataProyectoPorUsuario";
import { iDataProyectosResultadoMap } from "../../interfaces/UmmiaApps/iDataProyectosResultadoMap";
import { StyledTableCell, StyledTableRow } from "../../ui-components/StyledTables";
import EditableForm from "../Form/EditableForm";
import { ummiapps } from "../../data/ummiapps";
import { Spinner } from "../Loading/Spinner";
import editIcon from "./icons/editIcon.svg";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import { SortedDataPlanificaciones } from "../utils/SortedDataPlanificaciones";

interface Props {
  handleVerProyecto: (data: iDataProyectoPorUsuario) => void;
}

export const TablaPlanificacionesPorOrganizacion = ({ handleVerProyecto }: Props) => {
  const { handleDescargarProyecto } = useProjectsContext();
  const { user, authMode } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [vistaEdicion, setVistaEdicion] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [propuestaSeleccionada, setPropuestaSeleccionada] = useState<iDataProyectosResultadoMap>();
  const [propuestaKeys, setPropuestaKeys] = useState<string[]>([]);
  const [proyectosOrganizacionales, setProyectosOrganizacionales] = useState<iDataProyectosResultadoMap[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProposal, setSelectedProposal] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const {
    cargando,
    handleUploadToSchoology,
  } = useActividadesContext();

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    proyecto: iDataProyectoPorUsuario
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedProposal(proyecto.propuesta);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedProposal(null);
  };



  useEffect(() => {
    const load = async () => {
      if (user && user.id_organizacion) {
        setIsLoading(true);
        const result = await getProyectosByOrganizacionId(user.id_organizacion);
        if (result && result.length > 0) {
          const data = result.filter((r) => r.usuario?.email !== user.email);
          setProyectosOrganizacionales(data);
        }
        setIsLoading(false);
      }
    };
    load();
  }, [user]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value.toLowerCase());
    setPage(0);
  };

  const sortedData = () => {    
    return SortedDataPlanificaciones(proyectosOrganizacionales, orderBy, order);
  };

  const filteredData = () => {
    return sortedData().filter((job) => job.titulo?.toLowerCase().includes(searchQuery) || job.nivel?.toLowerCase().includes(searchQuery) || job.tipoEstructura?.toLowerCase().includes(searchQuery));
  };

  //Función para obtener el nombre de la aplicación según el tipo de estructura
  const getNombreApp = (tipoEstructura: string): string => {
    const componente = ummiapps.find((app: any) => app.tipoEstructura === tipoEstructura);
    return componente?.nombre || tipoEstructura;
  };

  return (
    <>
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Typography variant="h4" fontFamily={"Montserrat"} fontWeight={"Bold"} color={"#336B87"}>
          Recursos de mi organización
        </Typography>
        <TextField
          sx={{ mt: 4 }}
          fullWidth
          id="search-organizational-projects"
          label="Buscar..."
          variant="outlined"
          disabled={isLoading || proyectosOrganizacionales.length === 0}
          size="small"
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading && <Spinner msg="Cargando registros, por favor espere..." />}

      {isLoading === false && (
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ width: "100%", mb: 10, mt: 5 }}>
              <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table sx={{ minWidth: 500 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="left">N°</StyledTableCell>
                      {["titulo", "nivel", "tipoEstructura", "fechaCreacion", "calificacion"].map((column) => (
                        <StyledTableCell key={column} align="left" onClick={() => handleRequestSort(column)}>
                          {column === "titulo" && "Título"}
                          {column === "nivel" && "Nivel"}
                          {column === "tipoEstructura" && "Ummia APP"}
                          {column === "fechaCreacion" && "Fecha"}
                          {column === "calificacion" && "Valoración"}
                          {orderBy === column ? order === "asc" ? <ArrowDropUpIcon /> : <ArrowDropDownIcon /> : null}
                        </StyledTableCell>
                      ))}
                      <StyledTableCell align="center">Autor</StyledTableCell>
                      <StyledTableCell align="center">Acciones</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!isLoading &&
                      filteredData()
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((proyecto, index) => (
                          <StyledTableRow key={proyecto.resultadoPropuestaId}>
                            <StyledTableCell align="left">{index + 1 + page * rowsPerPage}</StyledTableCell>
                            <StyledTableCell align="left">{proyecto.titulo}</StyledTableCell>
                            <StyledTableCell align="left">{proyecto.nivel}</StyledTableCell>
                            <StyledTableCell align="left">{getNombreApp(proyecto.tipoEstructura)}</StyledTableCell>
                            <StyledTableCell align="left">{new Date(proyecto.fechaCreacion).toLocaleString()}</StyledTableCell>
                            <StyledTableCell align="left">
                              <Rating name="read-only" value={proyecto.calificacion} readOnly />
                            </StyledTableCell>
                            <StyledTableCell align="center">{proyecto.usuario?.name}</StyledTableCell>
                            <StyledTableCell align="center">
                              <Tooltip title="Editor"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#1D3D4D",
                                      color: "#FFF",
                                      fontFamily: "Montserrat",
                                      fontSize: "0.875rem",
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#1D3D4D",
                                    },
                                  },
                                }}>
                                <IconButton onClick={() => {
                                  handleVerProyecto(proyecto)
                                }} size="small" sx={{ color: '#336B87' }}>
                                  <Box
                                    component="img"
                                    src={editIcon}
                                    alt="Editar"
                                    sx={{ width: 28, height: 28 }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <>
                                <Tooltip
                                  title="Exportar / Compartir"
                                  arrow
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#1D3D4D",
                                        color: "#FFF",
                                        fontFamily: "Montserrat",
                                        fontSize: "0.875rem",
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                      },
                                    },
                                    arrow: { sx: { color: "#1D3D4D" } },
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    disabled={cargando}
                                    onClick={(e) => handleOpenMenu(e, proyecto)}
                                    sx={{ borderRadius: 5, border: "0px solid #336B87", color: '#336B87' }}
                                  >
                                    <CreateNewFolderRoundedIcon sx={{ fontSize: 25 }} />
                                  </Button>
                                </Tooltip>

                                <Menu
                                  anchorEl={anchorEl}
                                  open={open}
                                  onClose={handleClose}
                                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                                  transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                                  elevation={0}
                                  sx={{ fontFamily: "Montserrat" }}
                                  PaperProps={{
                                    sx: {
                                      bgcolor: "#1D3D4D",
                                      borderRadius: 5,
                                      fontFamily: "Montserrat",
                                      color: "#FFF"
                                    },
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      if (selectedProposal) {
                                        handleDescargarProyecto(selectedProposal);
                                      }
                                      handleClose();
                                    }}
                                    sx={{
                                      fontFamily: "Montserrat",
                                      transition: "background-color .15s ease, color .15s ease, padding-left .15s ease",
                                      "&:hover": {
                                        bgcolor: "#25485A",
                                        color: "#F8AD0C",
                                        pl: 2.5,
                                      },
                                      "&.Mui-focusVisible": {
                                        bgcolor: "#25485A",
                                        color: "#F8AD0C",
                                      },
                                      position: "relative",
                                      "&:hover::before, &.Mui-focusVisible::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 6,
                                        bottom: 6,
                                        width: 4,
                                        borderRadius: 2,
                                        backgroundColor: "#F8AD0C",
                                      },
                                    }}>Exportar PDF</MenuItem>
                                  <MenuItem
                                    disabled={authMode !== "LTI"}
                                    sx={{ bgcolor: "#7a7a7a75" }}
                                    onClick={handleUploadToSchoology}
                                  >
                                    Compartir con Schoology
                                  </MenuItem>
                                </Menu>
                              </>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    {proyectosOrganizacionales.length === 0 && (
                      <StyledTableRow>
                        <StyledTableCell align="left">No hay registros.</StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                showFirstButton
                showLastButton
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={proyectosOrganizacionales.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Grid>
        </Grid >
      )}

      {
        vistaEdicion && !isLoading && propuestaSeleccionada && (
          <EditableForm
            index={0}
            itemProyecto={propuestaSeleccionada}
            labels={propuestaKeys}
            handleCancelar={() => setVistaEdicion(false)}
            handleGuardar={(updatedProyecto: any) => {
              setVistaEdicion(false);
            }}
          />
        )
      }
    </>
  );
};

export default TablaPlanificacionesPorOrganizacion;
